import Dexie, { Table } from 'dexie';
import { GameState, TeamInLeague, Fixture, Cup, ManagerHistoryEntry, TransferHistoryRecord, NewsItem, League } from '../domain/entities';
import { GAME_VERSION } from '../version';
import { mensGameConfig } from '../data/mens/config';
import { womensGameConfig } from '../data/womens/config';

// --- NEW DB Schema ---
export interface SaveSlot {
    id?: number;
    name: string;
    timestamp: Date;
    gameState: GameState;
}

export interface SaveFile {
    id: number;
    name: string;
    timestamp: Date;
    version: string;
    playerTeamName: string;
    season: number;
    currentWeek: number;
}

class AppDB extends Dexie {
    saves!: Table<SaveSlot, number>;

    constructor() {
        super('PixelSoccerManagerDB_Saves');
        (this as Dexie).version(1).stores({
            saves: '++id, &name, timestamp',
        });
    }
}

export const db = new AppDB();

// --- Repository Logic ---

export const hasAutosave = async (): Promise<boolean> => {
    const autosave = await db.saves.get({ name: '_autosave' });
    return !!autosave;
};

export const clearAllSaves = async (): Promise<void> => {
    await db.saves.clear();
};

export const getAutosave = async (): Promise<GameState | null> => {
    const save = await db.saves.get({ name: '_autosave' });
    return save ? save.gameState : null;
};

export const saveAutosave = async (gameState: GameState): Promise<void> => {
    const existing = await db.saves.get({ name: '_autosave' });
    const saveData: SaveSlot = {
        name: '_autosave',
        timestamp: new Date(),
        gameState: gameState,
    };
    if (existing?.id) {
        saveData.id = existing.id;
    }
    await db.saves.put(saveData);
};

export const listSaves = async (): Promise<SaveFile[]> => {
    const allSaves = await db.saves
        .filter(save => save.name !== '_autosave')
        .reverse()
        .sortBy('timestamp');
        
    return allSaves.map(s => ({
        id: s.id!,
        name: s.name,
        timestamp: s.timestamp,
        version: s.gameState.version,
        playerTeamName: s.gameState.playerTeamName,
        season: s.gameState.season,
        currentWeek: s.gameState.currentWeek,
    }));
};

export const getSave = async (id: number): Promise<GameState | null> => {
    const save = await db.saves.get(id);
    return save ? save.gameState : null;
};

export const saveGame = async (id: number | null, name: string, gameState: GameState): Promise<void> => {
    const saveData: SaveSlot = {
        name,
        timestamp: new Date(),
        gameState,
    };
    if (id) {
        saveData.id = id;
    } else {
        const existing = await db.saves.get({ name });
        if (existing?.id) {
            saveData.id = existing.id;
        }
    }
    await db.saves.put(saveData);
};

export const deleteSave = async (id: number): Promise<void> => {
    await db.saves.delete(id);
};


// --- Legacy Save Import Logic ---

interface LegacyMeta { id: number; version: string; gameStarted: boolean; season: number; currentWeek: number; playerTeamName: string; playerLeagueName: string; gameOver: boolean; gameType: 'mens' | 'womens'; }
type LegacyStorableLeague = Omit<League, 'teams'>;
class LegacyAppDB extends Dexie {
    meta!: Table<LegacyMeta, number>; leagues!: Table<LegacyStorableLeague, string>; teams!: Table<TeamInLeague & { leagueName: string }, string>; fixtures!: Table<Fixture & { leagueName: string }, number>; cups!: Table<Cup, string>; playersForSale!: Table<{ playerId: number }, number>; managerHistory!: Table<ManagerHistoryEntry, number>; transferHistory!: Table<TransferHistoryRecord, number>; news!: Table<NewsItem, string>;
    constructor() {
        super('PixelSoccerManagerDB');
        (this as Dexie).version(3).stores({
            meta: 'id',
            leagues: '&name',
            teams: '&name, leagueName',
            fixtures: '++id, week, leagueName, home, away',
            cups: '&name',
            playersForSale: '++id, playerId',
            managerHistory: '++id, season',
            transferHistory: '++id, &[season+week+playerName+fromTeam+toTeam+fee]',
            news: '&id, [season+week]',
        });
    }
}

export const hasLegacySave = async (): Promise<boolean> => {
    return await Dexie.exists('PixelSoccerManagerDB');
}

export const importLegacySave = async (): Promise<void> => {
    if (!(await hasLegacySave())) return;

    console.log("Legacy save detected. Starting import process...");
    const legacyDb = new LegacyAppDB();

    try {
        const [meta, leaguesData, teams, fixturesData, cups, playersForSale, managerHistory, transferHistory, news] = await (legacyDb as Dexie).transaction('r', (legacyDb as Dexie).tables, () => {
            return Promise.all([
                legacyDb.meta.get(0),
                legacyDb.leagues.toArray(),
                legacyDb.teams.toArray(),
                legacyDb.fixtures.toArray(),
                legacyDb.cups.toArray(),
                legacyDb.playersForSale.toArray(),
                legacyDb.managerHistory.toArray(),
                legacyDb.transferHistory.toArray(),
                legacyDb.news.toArray(),
            ]);
        });
        
        if (!meta || !meta.gameStarted) {
            console.log("Legacy save is empty or invalid. Deleting.");
            await Dexie.delete('PixelSoccerManagerDB');
            return;
        };

        const leagues: Record<string, League> = {};
        for (const league of leaguesData) leagues[league.name] = { ...league, teams: [] };
        for (const team of teams) {
            const { leagueName, ...restOfTeam } = team;
            if (leagues[leagueName]) leagues[leagueName].teams.push(restOfTeam as TeamInLeague);
        }
        const fixtures: Record<string, Fixture[]> = {};
        for (const fixture of fixturesData) {
            const { leagueName, ...restOfFixture } = fixture;
            if (!fixtures[leagueName]) fixtures[leagueName] = [];
            fixtures[leagueName].push(restOfFixture);
        }

        const gameConfig = meta.gameType === 'womens' ? womensGameConfig : mensGameConfig;
        const gameState: GameState = {
            version: meta.version, gameStarted: true, season: meta.season, currentWeek: meta.currentWeek, playerTeamName: meta.playerTeamName, playerLeagueName: meta.playerLeagueName, gameOver: meta.gameOver,
            leagues,
            fixtures,
            cups: cups.reduce((acc, cup) => ({...acc, [cup.name]: cup}), {} as Record<string, Cup>),
            playersForSale: playersForSale.map(p => p.playerId),
            managerHistory, transferHistory, news, gameType: meta.gameType || 'mens',
            config: gameConfig,
            debugFlags: {
                adsDisabled: false,
                moneyAdded: false,
                simulationUsed: false,
                aiPlayerUsed: false,
                alwaysWin: false,
                showHiddenInfo: false,
            },
        };

        await saveAutosave(gameState);
        console.log("Legacy save successfully imported into autosave.");

        await Dexie.delete('PixelSoccerManagerDB');
        console.log("Legacy database deleted.");

    } catch (e) {
        console.error("Failed to import legacy save game:", e);
        // Don't delete if import fails, so user can report issue.
    }
};